import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowLeft, Upload, CheckCircle, User, Building2, FileText, Loader2 } from 'lucide-react';
import { API_BASE } from '../apiBase';
import { CURP_RE, EMAIL_RE, CP_RE, PHONE_RE, MAX_FILE_MB, MESSAGES, onlyDigits, cleanCurp, fileError } from '../validation';

const FieldError = ({ name, msg }) =>
  msg ? <p className="text-red-400 text-xs mt-1" data-testid={`error-${name}`}>{msg}</p> : null;

const FileInput = ({ id, label, onChange, file, error, errKey }) => (
  <div>
    <Label className="text-slate-200 mb-2 block">{label}</Label>
    <label
      htmlFor={id}
      className={`flex items-center gap-3 cursor-pointer bg-black border ${error ? 'border-red-500' : 'border-slate-700'} hover:border-yellow-500 rounded-lg px-4 py-3 transition-colors duration-200`}
    >
      <Upload className="h-5 w-5 text-yellow-400 flex-shrink-0" />
      <span className="text-slate-300 text-sm truncate">
        {file ? file.name : `Selecciona un archivo (JPG, PNG o PDF, máx. ${MAX_FILE_MB} MB)`}
      </span>
      <input
        id={id}
        data-testid={id}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
        className="hidden"
        onChange={(e) => onChange(e.target.files[0])}
      />
    </label>
    <FieldError name={errKey} msg={error} />
  </div>
);

const EMPTY = {
  nombre: '', curp: '', razon_social: '', calle: '', municipio: '', ciudad: '', codigo_postal: '',
  celular: '', email: '', tipo_identificacion: 'ine', website: '',
};

const requiredDocs = (tipo, idType) => (tipo === 'moral' ? ['constancia'] : idType === 'ine' ? ['doc1', 'doc2'] : ['doc1']);

const validate = (tipo, form, files) => {
  const e = {};
  if (tipo === 'fisica') {
    if (form.nombre.trim().length < 3) e.nombre = MESSAGES.required;
    if (!CURP_RE.test(form.curp)) e.curp = MESSAGES.curp;
  } else if (form.razon_social.trim().length < 2) e.razon_social = MESSAGES.required;
  ['calle', 'municipio', 'ciudad'].forEach((k) => { if (!form[k].trim()) e[k] = MESSAGES.required; });
  if (!CP_RE.test(form.codigo_postal)) e.codigo_postal = MESSAGES.cp;
  if (!PHONE_RE.test(form.celular)) e.celular = MESSAGES.phone;
  if (!EMAIL_RE.test(form.email.trim())) e.email = MESSAGES.email;
  requiredDocs(tipo, form.tipo_identificacion).forEach((k) => { const m = fileError(files[k]); if (m) e[k] = m; });
  return e;
};

const SANITIZE = {
  codigo_postal: (v) => onlyDigits(v, 5),
  celular: (v) => onlyDigits(v, 10),
  curp: cleanCurp,
};

export default function AccountForm() {
  const navigate = useNavigate();
  const [tipo, setTipo] = useState('fisica');
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState({ doc1: null, doc2: null, constancia: null });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const clearErr = (k) => setErrors((p) => ({ ...p, [k]: '' }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: SANITIZE[name] ? SANITIZE[name](value) : value });
    clearErr(name);
  };

  const pickFile = (key) => (f) => {
    if (!f) return;
    const m = fileError(f);
    setErrors((p) => ({ ...p, [key]: m }));
    setFiles((p) => ({ ...p, [key]: m ? null : f }));
  };

  const inputCls = (k) => `bg-black text-white ${errors[k] ? 'border-red-500' : 'border-slate-700'}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errs = validate(tipo, form, files);
    setErrors(errs);
    if (Object.keys(errs).length) {
      setError('Revisa los campos marcados en rojo.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      ['calle', 'municipio', 'ciudad', 'codigo_postal', 'celular', 'website'].forEach((k) => fd.append(k, form[k]));
      fd.append('tipo', tipo);
      fd.append('email', form.email.trim());

      if (tipo === 'fisica') {
        fd.append('nombre', form.nombre);
        fd.append('curp', form.curp);
        fd.append('tipo_identificacion', form.tipo_identificacion);
        if (form.tipo_identificacion === 'ine') {
          fd.append('files', files.doc1); fd.append('file_labels', 'INE (Frente)');
          fd.append('files', files.doc2); fd.append('file_labels', 'INE (Reverso)');
        } else {
          const lbl = form.tipo_identificacion === 'pasaporte' ? 'Pasaporte' : 'Identificación Migratoria';
          fd.append('files', files.doc1); fd.append('file_labels', lbl);
        }
      } else {
        fd.append('razon_social', form.razon_social);
        fd.append('files', files.constancia); fd.append('file_labels', 'Constancia de Situación Fiscal');
      }

      const res = await fetch(`${API_BASE}/api/account-request`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status !== 'success') {
        if (data.errors) setErrors(data.errors);
        throw new Error(data.detail || 'No se pudo enviar la solicitud. Revisa los datos e inténtalo de nuevo.');
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="account-success">
          <CheckCircle className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">¡Solicitud enviada!</h1>
          <p className="text-slate-300 mb-8">
            Recibimos tus datos y documentos. Te enviamos un correo de confirmación; nuestro equipo
            revisará tu solicitud y te contactará para activar tu cuenta.
          </p>
          <Button onClick={() => navigate('/')} className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold" data-testid="back-home-btn">
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const switchTipo = (t) => { setTipo(t); setErrors({}); setError(''); };

  return (
    <div className="min-h-screen bg-black py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors" data-testid="back-link">
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>

        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Abre tu Cuenta</h1>
        <p className="text-slate-300 mb-8">Completa tus datos y adjunta la documentación requerida.</p>

        <div className="grid grid-cols-2 gap-4 mb-8">
          <button
            type="button"
            data-testid="tipo-fisica"
            onClick={() => switchTipo('fisica')}
            className={`flex items-center gap-3 rounded-xl border p-4 transition-all duration-200 ${tipo === 'fisica' ? 'border-yellow-500 bg-yellow-500/10' : 'border-slate-700 bg-slate-900 hover:border-slate-500'}`}
          >
            <User className={`h-6 w-6 ${tipo === 'fisica' ? 'text-yellow-400' : 'text-slate-400'}`} />
            <span className={`font-semibold ${tipo === 'fisica' ? 'text-white' : 'text-slate-300'}`}>Persona Física</span>
          </button>
          <button
            type="button"
            data-testid="tipo-moral"
            onClick={() => switchTipo('moral')}
            className={`flex items-center gap-3 rounded-xl border p-4 transition-all duration-200 ${tipo === 'moral' ? 'border-yellow-500 bg-yellow-500/10' : 'border-slate-700 bg-slate-900 hover:border-slate-500'}`}
          >
            <Building2 className={`h-6 w-6 ${tipo === 'moral' ? 'text-yellow-400' : 'text-slate-400'}`} />
            <span className={`font-semibold ${tipo === 'moral' ? 'text-white' : 'text-slate-300'}`}>Persona Moral</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="relative space-y-6 bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8">
          <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
            <label htmlFor="website">Sitio web</label>
            <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={handleChange} data-testid="honeypot-website" />
          </div>

          {tipo === 'fisica' ? (
            <>
              <div>
                <Label htmlFor="nombre" className="text-slate-200 mb-2 block">Nombre completo *</Label>
                <Input id="nombre" name="nombre" data-testid="input-nombre" maxLength={200} value={form.nombre} onChange={handleChange} className={inputCls('nombre')} />
                <FieldError name="nombre" msg={errors.nombre} />
              </div>
              <div>
                <Label htmlFor="curp" className="text-slate-200 mb-2 block">CURP * <span className="text-slate-500 text-xs">({form.curp.length}/18)</span></Label>
                <Input id="curp" name="curp" data-testid="input-curp" maxLength={18} value={form.curp} onChange={handleChange} className={`${inputCls('curp')} uppercase tracking-wider`} placeholder="Ej. GODE561231HDFRRN09" />
                <FieldError name="curp" msg={errors.curp} />
              </div>
            </>
          ) : (
            <div>
              <Label htmlFor="razon_social" className="text-slate-200 mb-2 block">Razón Social *</Label>
              <Input id="razon_social" name="razon_social" data-testid="input-razon-social" maxLength={200} value={form.razon_social} onChange={handleChange} className={inputCls('razon_social')} />
              <FieldError name="razon_social" msg={errors.razon_social} />
            </div>
          )}

          <div>
            <Label className="text-slate-200 mb-2 block">Dirección *</Label>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Input id="calle" name="calle" data-testid="input-calle" maxLength={200} value={form.calle} onChange={handleChange} className={inputCls('calle')} placeholder="Calle y número" />
                <FieldError name="calle" msg={errors.calle} />
              </div>
              <div>
                <Input id="municipio" name="municipio" data-testid="input-municipio" maxLength={120} value={form.municipio} onChange={handleChange} className={inputCls('municipio')} placeholder="Municipio / Alcaldía" />
                <FieldError name="municipio" msg={errors.municipio} />
              </div>
              <div>
                <Input id="ciudad" name="ciudad" data-testid="input-ciudad" maxLength={120} value={form.ciudad} onChange={handleChange} className={inputCls('ciudad')} placeholder="Ciudad / Estado" />
                <FieldError name="ciudad" msg={errors.ciudad} />
              </div>
              <div>
                <Input id="codigo_postal" name="codigo_postal" data-testid="input-codigo-postal" inputMode="numeric" maxLength={5} value={form.codigo_postal} onChange={handleChange} className={inputCls('codigo_postal')} placeholder="Código Postal (5 dígitos)" />
                <FieldError name="codigo_postal" msg={errors.codigo_postal} />
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="celular" className="text-slate-200 mb-2 block">Celular * <span className="text-slate-500 text-xs">(10 dígitos)</span></Label>
              <Input id="celular" name="celular" type="tel" inputMode="numeric" data-testid="input-celular" maxLength={10} value={form.celular} onChange={handleChange} className={inputCls('celular')} placeholder="5512345678" />
              <FieldError name="celular" msg={errors.celular} />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-200 mb-2 block">Email *</Label>
              <Input id="email" name="email" type="email" data-testid="input-email" maxLength={200} value={form.email} onChange={handleChange} className={inputCls('email')} />
              <FieldError name="email" msg={errors.email} />
            </div>
          </div>

          {tipo === 'fisica' ? (
            <>
              <div>
                <Label className="text-slate-200 mb-2 block">Tipo de identificación *</Label>
                <Select value={form.tipo_identificacion} onValueChange={(v) => { setForm({ ...form, tipo_identificacion: v }); setErrors((p) => ({ ...p, doc1: '', doc2: '' })); }}>
                  <SelectTrigger data-testid="select-identificacion" className="bg-black border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ine">INE (Frente y Reverso)</SelectItem>
                    <SelectItem value="pasaporte">Pasaporte</SelectItem>
                    <SelectItem value="migratoria">Identificación Migratoria</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.tipo_identificacion === 'ine' ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  <FileInput id="file-ine-frente" errKey="doc1" label="INE (Frente) *" file={files.doc1} error={errors.doc1} onChange={pickFile('doc1')} />
                  <FileInput id="file-ine-reverso" errKey="doc2" label="INE (Reverso) *" file={files.doc2} error={errors.doc2} onChange={pickFile('doc2')} />
                </div>
              ) : (
                <FileInput id="file-identificacion" errKey="doc1" label={form.tipo_identificacion === 'pasaporte' ? 'Pasaporte *' : 'Identificación Migratoria *'} file={files.doc1} error={errors.doc1} onChange={pickFile('doc1')} />
              )}
            </>
          ) : (
            <FileInput id="file-constancia" errKey="constancia" label="Constancia de Situación Fiscal *" file={files.constancia} error={errors.constancia} onChange={pickFile('constancia')} />
          )}

          {error && <p className="text-red-400 text-sm" data-testid="form-error">{error}</p>}

          <Button type="submit" disabled={submitting} data-testid="submit-application-btn" className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-6 text-lg">
            {submitting ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Enviando...</>) : (<><FileText className="h-5 w-5 mr-2" /> Enviar Solicitud</>)}
          </Button>
          <p className="text-xs text-slate-500 text-center">
            Al enviar aceptas nuestros Términos y el Aviso de Privacidad. Tus datos y documentos se envían de forma segura para su revisión.
          </p>
        </form>
      </div>
    </div>
  );
}
